import React, { useState } from "react";
import { Box, Typography, Paper, Container, Button } from "@mui/material";
import {
  Refresh,
  Email,
  CalendarToday,
  CreditCard,
  Verified,
} from "@mui/icons-material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { generatePaymentUrl } from "../../Utils/PaymentUtils";
import styles from "./SubscriptionExpiredPage.module.css";
import { useLocation } from "react-router-dom";
import { PAYMENT_TYPES, SKIP_PAYMENT_IF_ZERO_AMOUNT } from "../../Utils/Constants";
import { postData } from "../../Services/postData";
import { endpoints } from "../../Utils/UrlEnum";
import { AxiosResponse } from "axios";

interface SubscriptionExpiredPageProps {
  clientData?: any;
  planName?: string;
  expirationDate?: string;
  renewalPrice?: string;
  onRenew?: () => void;
}

const SubscriptionExpiredPage: React.FC<SubscriptionExpiredPageProps> = ({
  clientData: propsClientData,
  planName: propsplanName,
  expirationDate: propsExpirationDate,
  renewalPrice: propsRenewalPrice,
  onRenew,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const state = location.state;

  const planName = state?.planName;

  const formatDate = (dateString?: string): string => {
    if (!dateString) {
      return new Date().toLocaleDateString("ro-RO", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
    try {
      return new Date(dateString).toLocaleDateString("ro-RO", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const expirationDate = formatDate(state?.expirationDate);
  const renewalPrice = state?.renewalPrice;

  const handleRenew = async () => {
    if (!planName) {
      console.error("Missing renewal data", renewalPrice, planName);
      return;
    }

    if (onRenew) {
      onRenew();
      return;
    }

    try {
      setIsLoading(true);

      const clientData = state?.clientData;
      const clientId = state.clientId;

      const result = await generatePaymentUrl({
        clientId,
        clientData,
        amount: renewalPrice,
        orderDescription: `${Vocabulary.renewSubscription} - ${planName}`,
        saveOrderId: false,
        purpose: PAYMENT_TYPES.SUBSCRIPTION_RENEWAL,
        metadata: { subscriptionId: state?.subscriptionId },
      });

      if (
        SKIP_PAYMENT_IF_ZERO_AMOUNT &&
        result?.paymentSkipped &&
        result?.redirectUrl
      ) {
        // activate subscription directly
        await postData(endpoints.client.activateSubscription, {
          clientId,
          subscriptionId: state?.subscriptionId,
        }).then((res: AxiosResponse) => {
          if (res?.data)
            // redirect to login or account page
            window.location.href = result.redirectUrl;
        });
      }
    } catch (error) {
      console.error("Error initiating renewal payment:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactSupport = () => {
    window.location.href = `mailto:${Vocabulary.supportEmail}`;
  };

  return (
    <Box className={styles.container}>
      <Container
        style={{ paddingLeft: "1rem", paddingRight: "1rem", width: "50vw" }}
      >
        {/* Header with Logo */}
        <Box className={styles.header}>
          <Box
            component="img"
            src="/sigur-la-volan.png"
            alt="Sigur La Volan"
            className={styles.logo}
          />
        </Box>

        {/* Main Paper Container */}
        <Paper className={styles.mainPaper} elevation={6}>
          <Box className={styles.messageSection}>
            <Typography className={styles.messageText}>
              {Vocabulary.subscriptionExpiredMessage}
            </Typography>
          </Box>

          {/* Subscription Summary Section */}
          <Box className={styles.summaryItem}>
            <span className={styles.summaryLabel}>{Vocabulary.activePlan}</span>
            <Typography className={styles.planName}>
              <Verified className={styles.summaryIcon} />
              {planName}
            </Typography>
          </Box>

          <Box className={styles.summaryDivider} />

          <Box className={styles.summaryGrid}>
            <Box className={styles.summaryItem}>
              <span className={styles.summaryLabel}>
                {Vocabulary.expirationDate}
              </span>
              <Typography className={styles.expirationDate}>
                <CalendarToday className={styles.summaryIcon} />
                {expirationDate}
              </Typography>
            </Box>

            <Box className={styles.summaryItem}>
              <span className={styles.summaryLabel}>
                {Vocabulary.renewalPrice}
              </span>
              <Typography className={styles.renewalPrice}>
                <CreditCard className={styles.summaryIcon} />
                {`${renewalPrice} ${Vocabulary.currency}`}
              </Typography>
            </Box>
          </Box>

          {/* Renewal Section */}
          <div className={styles.renewSection}>
            <Button
              variant="contained"
              color="success"
              onClick={handleRenew}
              disabled={isLoading}
              startIcon={<Refresh />}
              className={styles.renewButton}
            >
              {isLoading
                ? `${Vocabulary.loading}...`
                : Vocabulary.renewSubscription}
            </Button>
          </div>

          {/* Support Section */}
          <div className={styles.supportSection}>
            <Typography className={styles.supportTitle}>
              {Vocabulary.contactSupportForHelp}
            </Typography>
            <Typography className={styles.supportInfo}>
              {Vocabulary.supportContactInfo}
            </Typography>
            <div className={styles.supportButtonContainer}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleContactSupport}
                startIcon={<Email />}
                className={styles.supportButton}
              >
                {`${Vocabulary.contact}: ${Vocabulary.supportEmail}`}
              </Button>
            </div>
          </div>
        </Paper>
      </Container>
    </Box>
  );
};

export default SubscriptionExpiredPage;
